<template>
  <div class="system-logs-container">
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">系统日志</h1>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">查看和筛选系统运行日志</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <!-- 左侧：日志文件列表 -->
      <div class="lg:col-span-1">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">日志文件</h2>

          <div v-if="loadingFiles" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-2">加载中...</p>
          </div>

          <div v-else-if="logFiles.length === 0" class="text-center py-8">
            <p class="text-sm text-gray-500">暂无日志文件</p>
          </div>

          <div v-else class="space-y-2">
            <div v-for="file in logFiles" :key="file.filename" @click="selectFile(file.filename)"
              class="p-3 rounded-lg cursor-pointer transition-colors" :class="selectedFile === file.filename
                ? 'bg-blue-50 dark:bg-blue-900 border-2 border-blue-500'
                : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-2 border-transparent'">
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                    {{ file.filename }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ formatFileSize(file.size) }}
                  </p>
                </div>
                <font-awesome-icon v-if="selectedFile === file.filename" :icon="['fas', 'check-circle']"
                  class="text-blue-500 ml-2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：日志内容和筛选 -->
      <div class="lg:col-span-3">
        <!-- 筛选条件 -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <!-- 日志级别 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                日志级别
              </label>
              <select v-model="filters.level" @change="loadLogContent" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">全部</option>
                <option value="DEBUG">DEBUG</option>
                <option value="INFO">INFO</option>
                <option value="WARNING">WARNING</option>
                <option value="ERROR">ERROR</option>
              </select>
            </div>

            <!-- 任务 ID -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                任务筛选
              </label>
              <select v-model="filters.task_id" @change="loadLogContent" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">全部</option>
                <option value="reset-quotas">重置配额</option>
                <option value="check-expired-subscriptions">检查过期订阅</option>
                <option value="sync-redis-to-db">同步 Redis</option>
              </select>
            </div>

            <!-- 关键词搜索 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                关键词搜索
              </label>
              <input v-model="filters.search" @keyup.enter="loadLogContent" type="text" placeholder="输入关键词..." class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>

            <!-- 显示行数 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                显示行数
              </label>
              <select v-model.number="filters.lines" @change="loadLogContent" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option :value="50">50 行</option>
                <option :value="100">100 行</option>
                <option :value="200">200 行</option>
                <option :value="500">500 行</option>
                <option :value="1000">1000 行</option>
              </select>
            </div>

            <!-- 操作按钮 -->
            <div class="flex items-end gap-2">
              <button @click="loadLogContent" :disabled="!selectedFile || loadingContent"
                class="primary-btn flex-1 gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                查询
              </button>
              <button @click="toggleAutoRefresh" :class="autoRefresh
                ? 'primary-btn'
                : 'cancel-btn'"
                :title="autoRefresh ? '停止自动刷新' : '开启自动刷新'">
                <font-awesome-icon :icon="['fas', autoRefresh ? 'pause' : 'play']" />
              </button>
            </div>
          </div>
        </div>

        <!-- 日志内容 -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
              日志内容
              <span v-if="logContent" class="text-sm font-normal text-gray-500 ml-2">
                (共 {{ logContent.totalLines }} 行，显示 {{ logContent.lines.length }} 行)
              </span>
            </h2>
          </div>

          <div v-if="!selectedFile" class="text-center py-16">
            <font-awesome-icon :icon="['fas', 'file-alt']" class="text-6xl text-gray-300 mb-4" />
            <p class="text-gray-500">请选择一个日志文件</p>
          </div>

          <div v-else-if="loadingContent" class="text-center py-16">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-4">加载日志内容中...</p>
          </div>

          <div v-else-if="!logContent || logContent.lines.length === 0" class="text-center py-16">
            <font-awesome-icon :icon="['fas', 'inbox']" class="text-6xl text-gray-300 mb-4" />
            <p class="text-gray-500">没有找到匹配的日志</p>
          </div>

          <div v-else class="bg-gray-900 rounded-lg p-4 overflow-x-auto log-content-container">
            <div v-for="line in logContent.lines" :key="line.lineNumber"
              class="font-mono text-sm mb-1 whitespace-pre-wrap break-all log-line">
              <!-- 行号 -->
              <span class="text-gray-500 select-none mr-2 line-number">{{ line.lineNumber }}:</span>

              <!-- 时间戳 -->
              <span v-if="line.timestamp" class="text-blue-400 mr-1 timestamp">{{ line.timestamp }}</span>
              <span v-if="line.timestamp" class="text-gray-600 mr-1">|</span>

              <!-- 日志级别 -->
              <span v-if="line.level" :class="getLevelColorClass(line.level)" class="mr-1 level">{{ formatLevel(line.level) }}</span>
              <span v-if="line.level" class="text-gray-600 mr-1">|</span>

              <!-- 模块名 -->
              <span v-if="line.module" class="text-gray-400 mr-1 module">{{ line.module }}</span>
              <span v-if="line.module" class="text-gray-600 mr-1">|</span>

              <!-- 日志消息 -->
              <span :class="getMessageColorClass(line.level)" class="message">{{ line.message }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { getLogFilesAPI, getLogContentAPI } from '@/api/admin/logs'
import type { LogFileInfo, LogContentResponseData, LogContentParams } from '@/types/admin/logs'
import toaster from '@/utils/toaster'

// 状态管理
const logFiles = ref<LogFileInfo[]>([])
const selectedFile = ref<string>('')
const logContent = ref<LogContentResponseData | null>(null)
const loadingFiles = ref(false)
const loadingContent = ref(false)
const autoRefresh = ref(false)
let refreshInterval: NodeJS.Timeout | null = null

// 筛选条件
const filters = ref<LogContentParams>({
  filename: '',
  lines: 100,
  level: '',
  search: '',
  task_id: '',
})

/**
 * 加载日志文件列表
 */
const loadLogFiles = async () => {
  loadingFiles.value = true
  try {
    const response = await getLogFilesAPI()
    if (response.status === 200 && response.data) {
      logFiles.value = response.data
      // 自动选择第一个文件
      if (logFiles.value.length > 0 && !selectedFile.value) {
        selectFile(logFiles.value[0].filename)
      }
    } else {
      toaster.error(response.message || '加载日志文件列表失败')
    }
  } catch (error) {
    console.error('加载日志文件列表失败:', error)
    toaster.error('加载日志文件列表失败')
  } finally {
    loadingFiles.value = false
  }
}

/**
 * 选择日志文件
 */
const selectFile = (filename: string) => {
  selectedFile.value = filename
  filters.value.filename = filename
  loadLogContent()
}

/**
 * 加载日志内容
 */
const loadLogContent = async () => {
  if (!selectedFile.value) {
    return
  }

  loadingContent.value = true
  try {
    const response = await getLogContentAPI(filters.value)
    if (response.status === 200 && response.data) {
      logContent.value = response.data
    } else {
      toaster.error(response.message || '加载日志内容失败')
      logContent.value = null
    }
  } catch (error) {
    console.error('加载日志内容失败:', error)
    toaster.error('加载日志内容失败')
    logContent.value = null
  } finally {
    loadingContent.value = false
  }
}

/**
 * 切换自动刷新
 */
const toggleAutoRefresh = () => {
  autoRefresh.value = !autoRefresh.value

  if (autoRefresh.value) {
    // 开启自动刷新（每 5 秒）
    refreshInterval = setInterval(() => {
      loadLogContent()
    }, 5000)
    toaster.success('已开启自动刷新（每 5 秒）')
  } else {
    // 关闭自动刷新
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
    toaster.info('已停止自动刷新')
  }
}

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * 获取日志级别的颜色类
 *
 * @param level - 日志级别（ERROR/WARNING/INFO/DEBUG）
 * @returns Tailwind CSS 颜色类名
 */
const getLevelColorClass = (level: string | null): string => {
  if (!level) return 'text-gray-300'

  const levelUpper = level.toUpperCase()
  switch (levelUpper) {
    case 'ERROR':
      return 'text-red-400 font-semibold'
    case 'WARNING':
      return 'text-yellow-400 font-semibold'
    case 'INFO':
      return 'text-green-400'
    case 'DEBUG':
      return 'text-blue-400'
    default:
      return 'text-gray-300'
  }
}

/**
 * 获取日志消息的颜色类
 *
 * 根据日志级别为消息文本应用不同的颜色
 * ERROR 和 WARNING 使用更柔和的颜色以提高可读性
 *
 * @param level - 日志级别
 * @returns Tailwind CSS 颜色类名
 */
const getMessageColorClass = (level: string | null): string => {
  if (!level) return 'text-gray-300'

  const levelUpper = level.toUpperCase()
  switch (levelUpper) {
    case 'ERROR':
      return 'text-red-300'
    case 'WARNING':
      return 'text-yellow-300'
    case 'INFO':
      return 'text-gray-300'
    case 'DEBUG':
      return 'text-gray-400'
    default:
      return 'text-gray-300'
  }
}

/**
 * 格式化日志级别显示
 *
 * 将日志级别格式化为固定宽度（8个字符），与 loguru 原始格式保持一致
 *
 * @param level - 日志级别
 * @returns 格式化后的日志级别字符串
 */
const formatLevel = (level: string | null): string => {
  if (!level) return ''

  // 使用 padEnd 确保级别字段宽度一致（8个字符）
  // 这样可以保持日志的对齐，提高可读性
  return level.toUpperCase().padEnd(8)
}

// 生命周期钩子
onMounted(() => {
  loadLogFiles()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<style scoped>
.system-logs-container {
  @apply p-6;
}

/* 日志内容容器 */
.log-content-container {
  max-height: 600px;
  overflow-y: auto;
}

/* 日志行样式 */
.log-line {
  line-height: 1.6;
  transition: background-color 0.15s ease;
}

/* 日志行悬停效果 */
.log-line:hover {
  @apply bg-gray-800;
}

/* 行号样式 */
.line-number {
  display: inline-block;
  min-width: 3.5rem;
  text-align: right;
  user-select: none;
}

/* 时间戳样式 */
.timestamp {
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}

/* 日志级别样式 */
.level {
  display: inline-block;
  min-width: 5rem;
  font-variant-numeric: tabular-nums;
}

/* 模块名样式 */
.module {
  display: inline-block;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 消息样式 */
.message {
  word-break: break-word;
}

/* 自定义滚动条样式 */
.log-content-container::-webkit-scrollbar {
  width: 8px;
}

.log-content-container::-webkit-scrollbar-track {
  @apply bg-gray-800;
}

.log-content-container::-webkit-scrollbar-thumb {
  @apply bg-gray-600 rounded;
}

.log-content-container::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-500;
}

/* Firefox 滚动条样式 */
.log-content-container {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #1f2937;
}
</style>
